/**
 * `SkillsGapNudge` — sibling of `SetupGuideCard` on `SitesList`
 * (2026-07-09 redesign; see `SetupGuideCard.tsx`'s docblock for the
 * full rationale). Surfaces "N skills are enabled but not callable"
 * as ONE categorized summary line, linking into Visibility's
 * `SkillsHealthPanel` — the single authoritative per-skill status list
 * (ADR-0129 §2.5). This component never repeats that list; duplicating
 * it here is exactly how the prior copy drifted (`no_fulfillment_connector`
 * read as "connect a fulfillment backend" even for a WooCommerce site
 * that just needed its store connected).
 *
 * Unlike `SetupGuideCard`, this is NOT dismissible and does not persist
 * any hide state. The milestone tracker is a one-time checklist
 * (dismiss-forever is correct for it); this is a live, recurring
 * signal — a merchant who resolves today's gap can trigger a new one
 * next quarter by enabling a different skill, and "Hide" on the prior
 * combined card would have silently trained them to never see that
 * again.
 *
 * Tracks the SAME site `SetupGuideCard` does (`activeSite`) —
 * one site's skills at a time, not an all-sites aggregate — and only
 * fetches once verified (`siteVerified`), since skill activation is
 * meaningless pre-verify.
 */

import { ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { SkillWithStatus } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { useSites } from '../../app/providers.js';
import type { View } from '../../app/view-router.js';
import {
  gatingReasonCategory,
  gatingReasonCategoryLabel,
  type GatingReasonCategory,
} from '../../lib/skill-status-copy.js';
import { useC } from '../../lib/theme.js';
import { Card } from '../../components/atoms/index.js';
import { siteVerified } from './SetupGuideCard.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

/**
 * Skills the merchant turned on that still can't be called by an agent.
 * The nudge counts every "enabled but not live" row — not just
 * `enabled_unbacked` — so the Sites tile's "N skills enabled but not
 * live" number agrees with Settings → Skills' `enabled - live` footer
 * tally (which includes `enabled_unsupported` COMING SOON rows and
 * `tier_gated` rows alongside `enabled_unbacked`).
 */
export function unbackedEnabledSkills(
  skills: readonly SkillWithStatus[],
): readonly SkillWithStatus[] {
  return skills.filter((s) => s.enabled && s.status !== 'live');
}

/**
 * Build the one-line summary: single category names it directly
 * ("3 skills enabled but not live: connect a store or fulfillment
 * endpoint to activate them."); multiple categories list each,
 * comma-joined. Pure + exported for unit tests.
 */
export function summarizeUnbackedSkills(
  skills: readonly SkillWithStatus[],
): string | null {
  const unbacked = unbackedEnabledSkills(skills);
  if (unbacked.length === 0) return null;
  const categories = new Set<GatingReasonCategory>();
  for (const s of unbacked) {
    if (s.gating_reason !== undefined) categories.add(gatingReasonCategory(s.gating_reason));
  }
  const labels = Array.from(categories, gatingReasonCategoryLabel);
  const noun = unbacked.length === 1 ? 'skill' : 'skills';
  const action = labels.length > 0 ? labels.join(', ') : 'finish setup';
  return `${unbacked.length} ${noun} enabled but not live: ${action} to activate them.`;
}

export interface SkillsGapNudgeProps {
  setView: (v: View) => void;
}

export function SkillsGapNudge({
  setView,
}: SkillsGapNudgeProps): JSX.Element | null {
  const C = useC();
  const api = useApi();
  const { activeSite, setActiveSite } = useSites();
  const [skills, setSkills] = useState<readonly SkillWithStatus[]>([]);

  const site = activeSite;
  const wantSkills = site !== null && siteVerified(site);

  useEffect(() => {
    if (!wantSkills || site === null) return;
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const items = await api.skills.listForSite(site.site_id);
        if (cancelled) return;
        setSkills(items);
      } catch {
        // Leave empty — the nudge just stays hidden rather than
        // guessing; the next mount retries.
      }
    })();
    return (): void => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, wantSkills, site?.site_id]);

  if (site === null) return null;
  const summary = summarizeUnbackedSkills(skills);
  if (summary === null) return null;

  return (
    <Card className="p-4 mb-8" data-testid="skills-gap-nudge">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <p
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            lineHeight: 1.55,
            margin: 0,
            flex: 1,
          }}
        >
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.amberFg,
              letterSpacing: 1,
              marginRight: 8,
            }}
          >
            SKILLS
          </span>
          {summary}
        </p>
        <button
          type="button"
          onClick={() => {
            setActiveSite(site);
            setView('readiness');
          }}
          className="flex items-center justify-center gap-2 px-4 py-2 self-start sm:self-auto"
          style={{
            background: 'transparent',
            color: C.text,
            border: `1px solid ${C.border}`,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 12,
            borderRadius: 2,
            whiteSpace: 'nowrap',
          }}
          data-testid="skills-gap-nudge-cta"
        >
          Review in Agent readiness <ArrowRight size={13} />
        </button>
      </div>
    </Card>
  );
}
